import { z } from 'zod';
import { ROLES } from './roles.js';

/* ---------------------------------------------------------------- primitives */

/** เบอร์โทรไทย: 0812345678 หรือ 021234567 */
export const thaiPhone = z
  .string()
  .trim()
  .regex(/^0\d{8,9}$/, 'เบอร์โทรไม่ถูกต้อง');

/** เลขบัตรประชาชน 13 หลัก พร้อมตรวจ checksum หลักที่ 13 */
export const nationalId = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .refine((v) => /^\d{13}$/.test(v), 'เลขบัตรประชาชนต้องเป็นตัวเลข 13 หลัก')
  .refine(isValidThaiNationalId, 'เลขบัตรประชาชนไม่ถูกต้อง');

export function isValidThaiNationalId(id: string): boolean {
  const digits = id.replace(/[\s-]/g, '');
  if (!/^\d{13}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(digits[i]) * (13 - i);
  return (11 - (sum % 11)) % 10 === Number(digits[12]);
}

/** วันที่ ค.ศ. รูปแบบ YYYY-MM-DD */
export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'รูปแบบวันที่ต้องเป็น YYYY-MM-DD')
  .refine((v) => !Number.isNaN(Date.parse(v)), 'วันที่ไม่ถูกต้อง');

/** จำนวนเงินเป็นสตางค์ — จำนวนเต็มไม่ติดลบ */
export const satang = z.number().int().min(0).max(99_999_999_999);

export const latitude = z.number().min(-90).max(90);
export const longitude = z.number().min(-180).max(180);

export const cursorPagination = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().max(64).optional(),
});

/* -------------------------------------------------------------------- staff */

export const staffStatusEnum = z.enum(['invited', 'active', 'suspended', 'resigned']);
export const roleEnum = z.enum(ROLES);

export const createStaffSchema = z.object({
  staffCode: z.string().trim().min(1).max(32),
  fullName: z.string().trim().min(1).max(200),
  nickname: z.string().trim().max(64).optional(),
  email: z.string().trim().email().max(255).optional(),
  // อีเมลติดต่อกับอีเมลที่ใช้ล็อกอินแยกกัน — แก้อีเมลติดต่อไม่ควรกระทบสิทธิ์เข้าระบบ
  googleEmail: z.string().trim().email().max(255).optional().or(z.literal('')),
  phone: thaiPhone.optional(),
  role: roleEnum.default('field_officer'),
  teamId: z.string().max(40).optional(),
  areaIds: z.array(z.string().max(40)).max(200).default([]),
  employedAt: isoDate.optional(),
});

export const updateStaffSchema = createStaffSchema
  .omit({ staffCode: true, role: true })
  .partial()
  .extend({
    role: roleEnum.optional(), // ตรวจสิทธิ์เพิ่มที่ Worker: เฉพาะ super_admin
  });

export const updateStaffStatusSchema = z.object({
  status: staffStatusEnum,
  reason: z.string().trim().max(500).optional(),
});

export const listStaffQuerySchema = cursorPagination.extend({
  q: z.string().trim().max(100).optional(),
  role: roleEnum.optional(),
  teamId: z.string().max(40).optional(),
  status: staffStatusEnum.optional(),
});

/* -------------------------------------------------------------- beneficiary */

export const beneficiaryStatusEnum = z.enum([
  'screening',
  'active',
  'monitoring',
  'graduated',
  'closed',
  'rejected',
  'deceased',
]);

export const beneficiaryCategoryEnum = z.enum([
  'elderly',
  'disabled',
  'child',
  'patient',
  'homeless',
  'low_income',
  'disaster',
  'other',
]);

export const consentSchema = z.object({
  dataProcessing: z.literal(true, {
    errorMap: () => ({ message: 'ต้องได้รับความยินยอมการเก็บข้อมูลก่อนบันทึก' }),
  }),
  sensitiveData: z.boolean().default(false),
  photoPublish: z.boolean().default(false),
  policyVersion: z.string().min(1).max(20),
  evidenceType: z
    .enum(['liff_checkbox', 'signed_form', 'verbal_witnessed'])
    .default('signed_form'),
  evidenceKey: z.string().max(255).optional(),
});

export const householdMemberSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  relation: z.string().trim().max(64).optional(),
  birthDate: isoDate.optional(),
  occupation: z.string().trim().max(120).optional(),
  monthlyIncome: satang.optional(),
  isDependent: z.boolean().default(true),
  healthNote: z.string().trim().max(1000).optional(),
});

export const createBeneficiarySchema = z.object({
  nationalId: nationalId.optional(),
  title: z.string().trim().max(32).optional(),
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  nickname: z.string().trim().max(64).optional(),
  birthDate: isoDate.optional(),
  gender: z.enum(['male', 'female', 'other', 'unknown']).optional(),
  phone: thaiPhone.optional(),
  phoneAlt: thaiPhone.optional(),

  addressLine: z.string().trim().max(500).optional(),
  areaId: z.string().max(40).optional(),
  postalCode: z.string().regex(/^\d{5}$/).optional(),
  lat: latitude.optional(),
  lng: longitude.optional(),
  locationNote: z.string().trim().max(500).optional(),

  category: beneficiaryCategoryEnum.optional(),
  householdSize: z.number().int().min(1).max(50).optional(),
  monthlyIncome: satang.optional(),

  status: beneficiaryStatusEnum.default('screening'),
  primaryOfficerId: z.string().max(40).optional(),
  source: z.enum(['line_request', 'walk_in', 'referral', 'outreach', 'other']).optional(),
  referredBy: z.string().trim().max(200).optional(),
  intakeDate: isoDate.optional(),

  notes: z.string().trim().max(4000).optional(),
  householdMembers: z.array(householdMemberSchema).max(30).default([]),
  consent: consentSchema,
});

export const updateBeneficiarySchema = createBeneficiarySchema
  .omit({ consent: true, householdMembers: true, status: true })
  .partial()
  .extend({
    status: beneficiaryStatusEnum.optional(),
    closedReason: z.string().trim().max(500).optional(),
  });

export const listBeneficiaryQuerySchema = cursorPagination.extend({
  q: z.string().trim().max(100).optional(),
  status: beneficiaryStatusEnum.optional(),
  category: beneficiaryCategoryEnum.optional(),
  areaId: z.string().max(40).optional(),
  officerId: z.string().max(40).optional(),
  riskLevel: z.enum(['low', 'medium', 'high', 'critical']).optional(),
});

/* --------------------------------------------------------------------- auth */

export const liffAuthSchema = z.object({
  idToken: z.string().min(10).max(4096),
  app: z.enum(['staff', 'donor']).default('staff'),
});

export const linkAccountSchema = z.object({
  idToken: z.string().min(10).max(4096),
  token: z.string().min(10).max(128),
});

/** ถอนตัวเองออกจากระบบตอนส่งมอบ — ต้องพิมพ์รหัสเจ้าหน้าที่ยืนยัน กันกดพลาด */
export const leaveSystemSchema = z.object({
  confirmStaffCode: z.string().trim().min(1).max(32),
  reason: z.string().trim().max(500).optional(),
});

/** โค้ดใช้ครั้งเดียวที่ callback ของ Google/LINE ส่งกลับมาให้หน้าเว็บแลกเป็น token */
export const ssoExchangeSchema = z.object({
  code: z.string().trim().min(10).max(128),
});

/* ------------------------------------------------------------------- export */

export type SsoExchangeInput = z.infer<typeof ssoExchangeSchema>;
export type LeaveSystemInput = z.infer<typeof leaveSystemSchema>;
export type CreateStaffInput = z.infer<typeof createStaffSchema>;
export type UpdateStaffInput = z.infer<typeof updateStaffSchema>;
export type CreateBeneficiaryInput = z.infer<typeof createBeneficiarySchema>;
export type UpdateBeneficiaryInput = z.infer<typeof updateBeneficiarySchema>;
export type ConsentInput = z.infer<typeof consentSchema>;
export type HouseholdMemberInput = z.infer<typeof householdMemberSchema>;
